import axios from "axios";
import { API_BASE_URL } from "../../config";
import { authHeaders } from "../utils/session";

/**
 * The tax rates the business currently charges.
 *
 * GST was 18 in three files and a toggle label until now. A rate written into
 * the code is a rate that keeps being charged after it changes — and it is
 * charged to a customer, on an invoice, so getting it from the master is not a
 * tidiness question.
 */

/** Reads whichever of the usual shapes the API answers with. */
const rowsOf = (body) => {
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.data?.data)) return body.data.data;
  return [];
};

/* The master sends `percent` as a string — "18.00", and "0.00" for a tax that is
   currently not charged. Zero is a real answer, so only an unparseable value
   becomes null. */
const rateOf = (row) => {
  const value = Number(row?.percent ?? row?.rate ?? row?.percentage ?? row?.value);
  return Number.isFinite(value) ? value : null;
};

/* `code` is the stable identifier ("gst", "tds"); `label` is what accounts typed
   and could be renamed. Matched in that order, with the older field names kept
   as a fallback so an earlier master shape still resolves. */
const typeOf = (row) =>
  String(row?.code ?? row?.label ?? row?.tax_type ?? row?.type ?? row?.name ?? "")
    .trim()
    .toUpperCase();

/**
 * @returns {{gst: number|null, tds: number|null, rows: Array}}
 *          A rate is `null` when the master does not carry it — never a guess,
 *          because a wrong number here bills the customer wrongly.
 */
export const fetchTaxRates = async () => {
  const res = await axios.get(`${API_BASE_URL}/tax-rates`, { headers: authHeaders() });
  const rows = rowsOf(res.data);

  /* Inactive rows are history — a rate that was charged once and should not be
     charged again. Only a row that says nothing about being active is trusted,
     since older masters have no such column. */
  const live = rows.filter((r) => r.is_active === undefined || Number(r.is_active) === 1);
  const find = (kind) => {
    const hit = live.find((r) => typeOf(r).includes(kind));
    return hit ? rateOf(hit) : null;
  };

  return { gst: find("GST"), tds: find("TDS"), rows: live };
};
